import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import { useRefresh } from '../../Hooks/useRefresh';
import { ImportContactsModal } from '../Tags/Tag';
import Modal from '../../Components/Modal';
import { MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TextInput } from '../../Components/Inputs/TextInput';
import Select from '../../Components/Inputs/Select';
import { Button } from '../../Components/Buttons';
import { AddTagsModal } from './Contact';

export const useContactMappingForTable = (contacts) => {
    const { formatDate } = useDateFormat();

    return contacts.map((contact) => {
        // Create a new object without the data and is_deleted properties
        const { data, is_deleted, ...contactWithoutUnwantedProps } = contact;
        return {
            ...contactWithoutUnwantedProps,
            key: contact.id,
            created_at: formatDate(contact.created_at)
        };
    });
}

export default function Contacts() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();
    const appState = useAppState();
    const { sidebarSearch } = appState;
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations('id', 'desc');
    const {refresh, refreshId} = useRefresh();
    const [openImportContacts, setOpenImportContacts] = useState(false);
    const [filters, setFilters] = useState([]);
    const [openAddTagsModal, setOpenAddTagsModal] = useState(false);
    const { page, pageSize } = pagination;
    const [tags, setTags] = useState([]);

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                setLoading(true);
                const response = await api.listContacts(page, pageSize, pagination.sortBy, pagination.sortOrder, sidebarSearch, filters);
                setContacts(response.data.contacts);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchContacts();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch]);

    useEffect(() => {
        const fetchTags = async () => {
            const response = await api.listTags(1, 9999);
            setTags(response.data.tags);
        }
        fetchTags();
    }, []);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    const handleDelete = async (contact) => {
        await api.deleteContact(contact.id);
        refresh();
    }

    const mappedContacts = useContactMappingForTable(contacts);

    const exportContacts = async () => {
        try {
            const response = await api.exportContacts(filters);
            
            // Check if the response has a Content-Disposition header
            for (const header of response.headers) {
                console.log(header);
            }

            // Try to get Content-Disposition header (may be blocked by CORS)
            const contentDisposition = response.headers.get('Content-Disposition');
            console.log('Content-Disposition header:', contentDisposition);
            
            // If Content-Disposition is not available due to CORS, we'll proceed anyway
            // since the server should be sending the attachment
            if (contentDisposition && contentDisposition.includes('attachment')) {
                // Create a blob from the response
                const blob = await response.blob();
                
                // Create a URL for the blob
                const url = window.URL.createObjectURL(blob);
                
                // Create a temporary link element and trigger download
                const link = document.createElement('a');
                link.href = url;
                
                // Extract filename from Content-Disposition header if available
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                const filename = filenameMatch ? filenameMatch[1] : 'contacts.csv';
                link.download = filename;
                
                // Append to body, click, and remove
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the blob URL
                window.URL.revokeObjectURL(url);
            } else {
                // Content-Disposition header not available (likely due to CORS)
                // Proceed with download anyway since the server should be sending the attachment
                console.log('Content-Disposition header not accessible, proceeding with download...');
                
                // Create a blob from the response
                const blob = await response.blob();
                
                // Create a URL for the blob
                const url = window.URL.createObjectURL(blob);
                
                // Create a temporary link element and trigger download
                const link = document.createElement('a');
                link.href = url;
                link.download = 'contacts.csv'; // Default filename
                link.style.display = 'none';
                
                // Append to body, click, and remove
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the blob URL
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error exporting contacts:', error);
        }
    }

    const filterType = [
        { id: 1, name: translate('contacts.filter_type.have'), value: 'contact' },
        { id: 2, name: translate('contacts.filter_type.did'), value: 'event' },
        { id: 3, name: translate('contacts.filter_type.is_in_tag'), value: 'tag' },
    ]

    const setFilterType = (type, index) => {
        const newFilters = [...filters];
        if (type === 'tag' && tags.length > 0) {
            newFilters[index] = { ...newFilters[index], tag_id: tags[0].id };
        }
        newFilters[index] = { ...newFilters[index], type: type };
        setFilters(newFilters);
    }

    const addNewFilter = () => {
        setFilters([...filters, { type: filterType[0].value, event_count_direction: eventCountOptions[0].value, event_count: 0, source: 'contact', variable: 'contact.', value_source: 'contact', operation: "EQUALS", value: "" }]);
    }

    const setFilterVariable = (variable, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable: newFilters[index].source + "." + variable };
        setFilters(newFilters);
    }

    const setFilterEventName = (eventName, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], event_name: eventName };
        setFilters(newFilters);
    }

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' },
        { id: 4, name: translate('automatisation.string_variable'), value: 'string_variable' },
        { id: 5, name: translate('automatisation.number_variable'), value: 'number_variable' },
        { id: 6, name: translate('automatisation.boolean_variable'), value: 'boolean_variable' }
    ]

    const getOperationOptions = (variableType) => {
        const operations = [
            { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
            { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
            ...(variableType === 'number' || variableType === 'number_variable' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
            ...(variableType === 'string' || variableType === 'string_variable' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
            ...(variableType === 'number_variable' || variableType === 'string_variable' || variableType === 'boolean_variable' ? [] : [{ id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' }, { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' }]),
        ];
        return operations;
    };

    const setFilterOperation = (operation, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], operation: operation, variable_type: variableTypes.find(type => type.value === newFilters[index].variable_type)?.value || variableTypes[0].value };
        setFilters(newFilters);
    }
    const setFilterValueSource = (source, index) => {
        const newFilters = [...filters];
        const newVariableValueWithoutSource = newFilters[index].value.split(".").slice(1).join(".");
        newFilters[index] = { ...newFilters[index], value_source: source.id, value: source.id + "." + newVariableValueWithoutSource };
        setFilters(newFilters);
    }
    const setFilterValue = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: newFilters[index].value_source + "." + value };
        setFilters(newFilters);
    }

    const setFilterValueWithoutSource = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: value };
        setFilters(newFilters);
    }

    const getSelectedFilterOperation = (filter) => {
        return getOperationOptions(filter.variable_type || variableTypes[0].value).find(operation => operation.value === filter.operation) || getOperationOptions(filter.variable_type || variableTypes[0].value)[0];
    }

    const setFilterVariableType = (variableType, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable_type: variableType, value: "", operation: "EQUALS" };
        setFilters(newFilters);
    }

    const removeFilter = (index) => {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
    }

    const setFilterEventCount = (eventCount, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], event_count: eventCount };
        setFilters(newFilters);
    }

    const setFilterInTheLast = (inTheLast, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], in_the_last: inTheLast };
        setFilters(newFilters);
    }

    const eventCountOptions = [
        { id: 1, name: translate('contacts.more_than'), value: 'more_than' },
        { id: 2, name: translate('contacts.less_than'), value: 'less_than' }
    ]

    const setFilterEventCountDirection = (eventCountDirection, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], event_count_direction: eventCountDirection };
        setFilters(newFilters);
    }

    const setFilterTag = (tag, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], tag_id: tag.id };
        setFilters(newFilters);
    }

    return (
        <>
            <Modal
                maxWidth="sm:max-w-2xl"
                open={openImportContacts}
                onClose={() => setOpenImportContacts(false)}
                title={translate('tag.import_contacts')}
                description={''}
                children={<ImportContactsModal open={openImportContacts} setOpen={setOpenImportContacts}  refresh={refresh} />}
            />
            <AddTagsModal allContact={true} filters={filters} refresh={refresh} open={openAddTagsModal} setOpen={setOpenAddTagsModal} />
            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="contacts">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <>
                        <div className="mb-2 border-b border-gray-200 pb-2">
                            <div className="flex flex-row mt-4 text-gray-900 items-center justify-between">
                                <div className="flex flex-row items-center cursor-pointer hover:text-gray-600" onClick={() => addNewFilter()}>
                                    <p className="text-sm font-bold">{translate('filters').slice(0,1).toUpperCase() + translate('filters').slice(1)}</p>
                                    <PlusIcon className="w-5 h-5  ml-2" />
                                </div>
                                <div>
                                    <Button onClick={refresh}>{translate('refresh')}</Button>
                                </div>
                                
                            </div>
                            <div className="flex flex-col">
                                {filters.map((filter, index) => (
                                    <div className="flex flex-row items-center gap-x-2">
                                        <span className="text-sm text-gray-600 font-bold">{translate('contacts.contact_who')}</span>
                                        <div className='w-40 mb-2'>
                                            <Select options={filterType} selected={filterType.find(type => type.value === filter.type) || filterType[0]} setSelected={(e) => setFilterType(e.value, index)} />
                                        </div>
                                        <div className="w-6 bg-gray-200 h-[1px]"></div>
                                        {filter.type === 'contact' && <div className="flex flex-row items-center gap-x-2">
                                            <TextInput className="mb-2" placeholder={translate('automatisation.variable')} value={filter.variable.split(".").slice(1).join(".")} onChange={(e) => setFilterVariable(e.target.value, index)} />
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            <div className='w-48 mb-2'>
                                                <Select options={getOperationOptions(filter.variable_type || variableTypes[0].value)} selected={getSelectedFilterOperation(filter)} setSelected={(e) => setFilterOperation(e.value, index)} />
                                            </div>
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            <div className='w-48 mb-2'>
                                                <Select options={variableTypes} selected={variableTypes.find(type => type.value === filter.variable_type) || variableTypes[0]} setSelected={(e) => setFilterVariableType(e.value, index)} />
                                            </div>
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            {(filter.variable_type === 'string_variable' || filter.variable_type === 'number_variable' || filter.variable_type === 'boolean_variable') && <div className="flex flex-row items-center gap-x-2">
                                                <TextInput className="mb-2" value={filter.value.split(".").slice(1).join(".")} onChange={(e) => setFilterValue(e.target.value, index)} />
                                            </div>}
                                            {(!filter.variable_type?.includes('variable') && getSelectedFilterOperation(filter).value != 'IS_NULL_OR_EMPTY' && getSelectedFilterOperation(filter).value != 'IS_NOT_NULL_OR_EMPTY') && <div className="flex flex-row items-center gap-x-2">
                                                <TextInput className="mb-2" value={filter.value} onChange={(e) => setFilterValueWithoutSource(e.target.value, index)} />
                                            </div>}
                                            <MinusIcon className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-300" onClick={() => removeFilter(index)} />
                                        </div>}
                                        {filter.type === 'event' && <div className="flex flex-row items-center gap-x-2">
                                            <TextInput className="mb-2" placeholder={translate('automatisation.event_name')} value={filter.event_name} onChange={(e) => setFilterEventName(e.target.value, index)} />
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            <div className='w-32 mb-2'>
                                                <Select options={eventCountOptions} selected={eventCountOptions.find(type => type.value === filter.event_count_direction) || eventCountOptions[0]} setSelected={(e) => setFilterEventCountDirection(e.value, index)} />
                                            </div>
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            <div className="flex flex-row items-center gap-x-2">
                                                <div className="w-16">
                                                    <TextInput className="mb-2" type="number" value={filter.event_count} onChange={(e) => setFilterEventCount(e.target.value, index)} />
                                                </div>
                                                <span className="text-sm text-gray-600 font-bold">{translate('contacts.events_time')}</span>
                                            </div>
                                            <div className="w-6 bg-gray-200 h-[1px]"></div>
                                            <div className="flex flex-row items-center gap-x-2">
                                                <span className="text-sm text-gray-600 font-bold">{translate('contacts.in_the_last')}</span>
                                                <div className="w-20">
                                                    <TextInput className="mb-2" type="number" value={filter.in_the_last} onChange={(e) => setFilterInTheLast(e.target.value, index)} />
                                                </div>
                                                <span className="text-sm text-gray-600 font-bold">{translate('contacts.days')}</span>
                                            </div>
                                            <MinusIcon className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-300" onClick={() => removeFilter(index)} />
                                        </div>}
                                        {filter.type === 'tag' && <div className="flex flex-row items-center gap-x-2">
                                            <div className='w-48 mb-2'>
                                                <Select options={tags} selected={tags.find(tag => tag.id === filter.tag_id) || tags[0]} setSelected={(e) => setFilterTag(e, index)} />
                                            </div>
                                            <MinusIcon className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-300" onClick={() => removeFilter(index)} />
                                        </div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Table
                            title={translate('contacts.title')}
                            description={translate('contacts.description')}
                            createButton={undefined}
                            data={mappedContacts}
                            translationKey="contacts.table"
                            columns={['external_id', 'created_at']}
                            pagination={pagination}
                            onSort={handleSort}
                            actions={[{
                                text: translate('contacts.add_tags'),
                                action: () => setOpenAddTagsModal(true)
                            },{
                                text: translate('contacts.import_contacts'),
                                action: () => setOpenImportContacts(true)
                            }, {
                                text: translate('contacts.export_contacts'),
                                action: () => exportContacts()
                            }]}
                            onRowClick={(row) => {
                                navigate(`/contacts/${row.external_id}`);
                            }}
                            dropdownGroups={[{
                                options: [{
                                    label: translate('delete'),
                                    icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => handleDelete(row)
                                }]
                            }]}
                        />
                        </>
                    )}
                </SidebarNav>
            </div>
        </>
    );
}